import React, { useState } from 'react';
import { CheckCircle2, CheckCircle, Circle, Plus, Pencil, Trash2, ChevronDown, ChevronUp, AlertCircle, Check, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { trackTaskCompletion } from '../../utils/analytics';
import { useAdminSettings } from '../../hooks/useAdminSettings';
import { API } from '../../config/api';
import { formatDate } from '../../utils/dateFormat';

// Helper function to render text with clickable links - inlined to ensure deployment
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // URL regex pattern - matches http and https URLs
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = text.split(urlPattern);
  
  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part && part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#80298F] hover:underline inline-flex items-center gap-1 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
          <ExternalLink className="h-3 w-3 inline-block flex-shrink-0" />
        </a>
      );
    }
    return part ? <span key={index}>{part}</span> : null;
  });
};

export function CaseTasks({
  caseId,
  caseData,
  tasks,
  clientData,
  staffList,
  user,
  onRefresh,
  isStaffOrAdmin
}) {
  // Fetch dynamic task templates from admin settings
  const { getTaskTemplatesByCaseType, loading: settingsLoading } = useAdminSettings();

  // Task creation/edit state
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskCreationMode, setTaskCreationMode] = useState('select');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templateConfigs, setTemplateConfigs] = useState({});
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_to: ''
  });

  // Collapse states
  const [overdueCollapsed, setOverdueCollapsed] = useState(false);
  const [pendingCollapsed, setPendingCollapsed] = useState(false);
  const [completedCollapsed, setCompletedCollapsed] = useState(true);

  // Get task templates based on case type from admin settings
  const getTaskTemplates = () => {
    if (!caseData) return [];
    return getTaskTemplatesByCaseType(caseData.case_type);
  };

  // Task handlers
  const openTaskCreationSelector = () => {
    setTaskCreationMode('select');
    setSelectedTemplates([]);
    setTemplateConfigs({});
    setShowCreateTaskModal(true);
  };

  const openCreateCustomTask = () => {
    setTaskForm({
      title: '',
      description: '',
      due_date: new Date().toISOString().split('T')[0],
      priority: 'medium',
      assigned_to: caseData?.client_id || user?.user_id || ''
    });
    setEditingTask(null);
    setTaskCreationMode('custom');
  };

  const openTemplateSelector = () => {
    setTaskCreationMode('template');
    setSelectedTemplates([]);
    setTemplateConfigs({});
  };

  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        const newSelected = prev.filter(id => id !== templateId);
        setTemplateConfigs(configs => {
          const newConfigs = { ...configs };
          delete newConfigs[templateId];
          return newConfigs;
        });
        return newSelected;
      } else {
        setTemplateConfigs(configs => ({
          ...configs,
          [templateId]: {
            due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            priority: 'medium',
            assigned_to: caseData?.client_id || ''
          }
        }));
        return [...prev, templateId];
      }
    });
  };

  const updateTemplateConfig = (templateId, field, value) => {
    setTemplateConfigs(configs => ({
      ...configs,
      [templateId]: {
        ...configs[templateId],
        [field]: value
      }
    }));
  };

  const handleCreateTemplatedTasks = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one task template');
      return;
    }

    const templates = getTaskTemplates();
    let successCount = 0;
    let failCount = 0;

    for (const templateId of selectedTemplates) {
      const template = templates.find(t => t.id === templateId);
      const config = templateConfigs[templateId];
      
      if (!template || !config) continue;

      try {
        const fullDescription = `${template.description}\n\nWhy this is needed: ${template.whyNeeded}`;
        
        const response = await fetch(`${API}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            case_id: caseId,
            title: template.title,
            description: fullDescription,
            due_date: new Date(config.due_date).toISOString(),
            priority: config.priority,
            assigned_to: config.assigned_to
          })
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} task${successCount > 1 ? 's' : ''} created successfully`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} task${failCount > 1 ? 's' : ''} failed to create`);
    }

    setShowCreateTaskModal(false);
    setSelectedTemplates([]);
    setTemplateConfigs({});
    setTaskCreationMode('select');
    onRefresh();
  };

  const openEditTaskModal = (task) => {
    setTaskForm({
      title: task.title,
      description: task.description || '',
      due_date: new Date(task.due_date).toISOString().split('T')[0],
      priority: task.priority,
      assigned_to: task.assigned_to
    });
    setEditingTask(task);
    setShowEditTaskModal(true);
  };

  const handleCreateTask = async () => {
    try {
      const response = await fetch(`${API}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          case_id: caseId,
          title: taskForm.title,
          description: taskForm.description,
          due_date: new Date(taskForm.due_date).toISOString(),
          priority: taskForm.priority,
          assigned_to: taskForm.assigned_to
        })
      });

      if (!response.ok) throw new Error('Failed to create task');
      toast.success('Task created successfully');
      setShowCreateTaskModal(false);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateTask = async () => {
    try {
      const response = await fetch(`${API}/tasks/${editingTask.task_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: taskForm.title,
          description: taskForm.description,
          due_date: new Date(taskForm.due_date).toISOString(),
          priority: taskForm.priority,
          assigned_to: taskForm.assigned_to
        })
      });

      if (!response.ok) throw new Error('Failed to update task');
      toast.success('Task updated successfully');
      setShowEditTaskModal(false);
      setEditingTask(null);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete task');
      toast.success('Task deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleTaskComplete = async (task) => {
    try {
      const response = await fetch(`${API}/tasks/${task.task_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed: !task.completed })
      });

      if (!response.ok) throw new Error('Failed to update task');
      
      if (!task.completed) {
        trackTaskCompletion(task.task_id);
      }
      
      toast.success(task.completed ? 'Task marked incomplete' : 'Task completed');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Categorize tasks
  const now = new Date();
  const overdueTasks = tasks.filter(t => !t.completed && new Date(t.due_date) < now);
  const pendingTasks = tasks.filter(t => !t.completed && new Date(t.due_date) >= now);
  const completedTasks = tasks.filter(t => t.completed);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderTaskItem = (task) => (
    <div
      key={task.task_id}
      className={`p-4 border rounded-lg ${task.completed ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-[#80298F]/30'} transition-colors`}
      data-testid={`task-${task.task_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <button
            onClick={() => handleToggleTaskComplete(task)}
            className={`mt-0.5 flex-shrink-0 transition-all duration-200 ${
              task.completed 
                ? 'text-green-500' 
                : 'text-gray-400 hover:text-[#80298F]'
            }`}
            data-testid={`toggle-task-${task.task_id}`}
            title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {task.completed ? (
              <div className="relative">
                <CheckCircle2 className="h-5 w-5 fill-green-500 text-green-500" />
              </div>
            ) : (
              <Circle className="h-5 w-5" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {task.title}
            </p>
            {task.description && (
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{renderTextWithLinks(task.description)}</p>
            )}
            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
              <span>Due: {formatDate(task.due_date)}</span>
              <span className={`px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}>
                {task.priority}
              </span>
            </div>
          </div>
        </div>
        {isStaffOrAdmin && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => openEditTaskModal(task)}
              className="p-1.5 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded transition-colors"
              title="Edit task"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteTask(task.task_id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete task"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderTaskSection = (title, taskList, collapsed, setCollapsed, icon, iconColor, badgeColor) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {React.createElement(icon, { className: `h-5 w-5 ${iconColor}` })}
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <span className={`text-sm px-2 py-0.5 rounded-full ${badgeColor}`}>
            {taskList.length}
          </span>
        </div>
        {collapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-4 space-y-3">
          {taskList.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No tasks</p>
          ) : (
            taskList.map(renderTaskItem)
          )}
        </div>
      )}
    </div>
  );

  const templates = getTaskTemplates();

  return (
    <>
      <div className="space-y-6" data-testid="tasks-content">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Tasks ({tasks.length})</h2>
          {isStaffOrAdmin && (
            <Button onClick={openTaskCreationSelector} className="bg-[#80298F] hover:bg-[#6b2278]">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          )}
        </div>

        {tasks.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No tasks yet</p>
            {isStaffOrAdmin && (
              <Button 
                onClick={openTaskCreationSelector}
                variant="link" 
                className="text-[#80298F] mt-2"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add first task
              </Button>
            )}
          </div>
        ) : (
          <>
            {overdueTasks.length > 0 && renderTaskSection(
              'Overdue',
              overdueTasks,
              overdueCollapsed,
              setOverdueCollapsed,
              AlertCircle,
              'text-red-500',
              'bg-red-100 text-red-800'
            )}
            {renderTaskSection(
              'Pending',
              pendingTasks,
              pendingCollapsed,
              setPendingCollapsed,
              Circle,
              'text-yellow-500',
              'bg-yellow-100 text-yellow-800'
            )}
            {renderTaskSection(
              'Completed',
              completedTasks,
              completedCollapsed,
              setCompletedCollapsed,
              CheckCircle,
              'text-green-500',
              'bg-green-100 text-green-800'
            )}
          </>
        )}
      </div>

      {/* Create Task Modal */}
      <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {taskCreationMode === 'select' ? 'Create New Task' : 
               taskCreationMode === 'template' ? 'Select Task Templates' : 'Create Custom Task'}
            </DialogTitle>
          </DialogHeader>

          {/* Selection Mode */}
          {taskCreationMode === 'select' && (
            <div className="space-y-4">
              <p className="text-gray-600">How would you like to create tasks?</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={openTemplateSelector}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#80298F] hover:bg-[#80298F]/5 transition-colors text-left"
                >
                  <CheckCircle className="h-8 w-8 text-[#80298F] mb-3" />
                  <h3 className="font-semibold text-gray-900">Use Templates</h3>
                  <p className="text-sm text-gray-500 mt-1">Choose from pre-built task templates for {caseData?.case_type?.replace(/_/g, ' ')} cases</p>
                </button>
                <button
                  onClick={openCreateCustomTask}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#80298F] hover:bg-[#80298F]/5 transition-colors text-left"
                >
                  <Plus className="h-8 w-8 text-[#80298F] mb-3" />
                  <h3 className="font-semibold text-gray-900">Create Custom</h3>
                  <p className="text-sm text-gray-500 mt-1">Create a task from scratch with your own title and details</p>
                </button>
              </div>
            </div>
          )}

          {/* Template Selection Mode */}
          {taskCreationMode === 'template' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">Select templates and configure due dates:</p>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {templates.map(template => {
                  const isSelected = selectedTemplates.includes(template.id);
                  const config = templateConfigs[template.id];
                  
                  return (
                    <div
                      key={template.id}
                      className={`border-2 rounded-lg transition-colors ${
                        isSelected ? 'border-[#80298F] bg-[#80298F]/5' : 'border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => toggleTemplateSelection(template.id)}
                        className="w-full p-4 text-left"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'border-[#80298F] bg-[#80298F]' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{template.title}</h4>
                            <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                          </div>
                        </div>
                      </button>
                      
                      {isSelected && config && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100 ml-8">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Due Date</Label>
                              <Input
                                type="date"
                                value={config.due_date}
                                onChange={(e) => updateTemplateConfig(template.id, 'due_date', e.target.value)}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Priority</Label>
                              <Select
                                value={config.priority}
                                onValueChange={(val) => updateTemplateConfig(template.id, 'priority', val)}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Low</SelectItem>
                                  <SelectItem value="medium">Medium</SelectItem>
                                  <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Assign To</Label>
                              <Select
                                value={config.assigned_to}
                                onValueChange={(val) => updateTemplateConfig(template.id, 'assigned_to', val)}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clientData && (
                                    <SelectItem value={clientData.user_id}>{clientData.name} (Client)</SelectItem>
                                  )}
                                  {staffList.map(staff => (
                                    <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {selectedTemplates.length > 0 && (
                <div className="bg-[#80298F]/10 rounded-lg p-3 text-sm">
                  <strong>{selectedTemplates.length}</strong> task{selectedTemplates.length > 1 ? 's' : ''} selected
                </div>
              )}
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setTaskCreationMode('select')}>Back</Button>
                <Button 
                  onClick={handleCreateTemplatedTasks} 
                  className="bg-[#80298F] hover:bg-[#6b2278]"
                  disabled={selectedTemplates.length === 0}
                >
                  Create {selectedTemplates.length} Task{selectedTemplates.length !== 1 ? 's' : ''}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Custom Task Creation Mode */}
          {taskCreationMode === 'custom' && (
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                  placeholder="Task title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                  placeholder="Task description"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={taskForm.priority}
                    onValueChange={(value) => setTaskForm({...taskForm, priority: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Assign To</Label>
                <Select
                  value={taskForm.assigned_to}
                  onValueChange={(value) => setTaskForm({...taskForm, assigned_to: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientData && (
                      <SelectItem value={clientData.user_id}>{clientData.name} (Client)</SelectItem>
                    )}
                    {staffList.map(staff => (
                      <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                    ))}
                    {user && <SelectItem value={user.user_id}>{user.name} (Me)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setTaskCreationMode('select')}>Back</Button>
                <Button onClick={handleCreateTask} className="bg-[#80298F] hover:bg-[#6b2278]">Create Task</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={showEditTaskModal} onOpenChange={setShowEditTaskModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                placeholder="Task title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Task description"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({...taskForm, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select
                value={taskForm.assigned_to}
                onValueChange={(value) => setTaskForm({...taskForm, assigned_to: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map(staff => (
                    <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                  ))}
                  {user && <SelectItem value={user.user_id}>{user.name} (Me)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTaskModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateTask} className="bg-[#80298F] hover:bg-[#6b2278]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CaseTasks;
